import log from 'loglevel';

//eslint-disable-next-line no-restricted-imports
import type DxfArrayScanner from '../../DxfArrayScanner';
//eslint-disable-next-line no-restricted-imports
import { serializeGroupValue } from '../../DxfArrayScanner';
//eslint-disable-next-line no-restricted-imports
import { groupIs } from '../../ParseHelpers';
//eslint-disable-next-line no-restricted-imports
import { DXFSymbol } from '../../constants';
import type { ApplicationDefinedGroups } from '../tables';
import {
  parseApplicationGroup,
  serializeApplicationDefinedGroups,
} from '../tables';

export type ILineType = {
  alignmentCode?: number; //alwys 65 for ascii letter A
  applicationGroups?: ApplicationDefinedGroups;
  description?: string;
  elementCount?: number;
  handle?: string;
  name: string;
  ownerHandle?: string;
  pattern?: string[]; //dash dot or space length per element
  patternLength?: number;
  patternTypes?: number[];
  shapeNumbers?: number[];
  standardFlags?: number;

  //Note: subMarkers insertion order must be maintained when serializing
  //subMarkers are necessary for eDrawings to import dxf we serialize
  //ltypes use 1 or 2 subMarker types. 3rd and 4th are added for insurance
  subMarkerCount?: number;
  subMarker1?: string;
  subMarker2?: string;
  subMarker3?: string;
  subMarker4?: string;
};

const lineTypePropertyFromCode = new Map<number, keyof ILineType>([
  [2, 'name'],
  [3, 'description'],
  [5, 'handle'],
  [40, 'patternLength'],
  [70, 'standardFlags'],
  [72, 'alignmentCode'],
  [330, 'ownerHandle'],
]);

const codeFromLineTypeProperty = new Map<keyof ILineType, number>(
  Array.from(lineTypePropertyFromCode.entries()).map(([code, property]) => [
    property,
    code,
  ]),
);

//delete special cases that are are handled differently when serializing and parsing
lineTypePropertyFromCode.delete(2);
codeFromLineTypeProperty.delete('patternLength');

codeFromLineTypeProperty.set('subMarker1', 100);
codeFromLineTypeProperty.set('subMarker2', 100);
codeFromLineTypeProperty.set('subMarker3', 100);
codeFromLineTypeProperty.set('subMarker4', 100);

export const parseLineTypes = (
  scanner: DxfArrayScanner,
): Record<string, ILineType> => {
  const ltypes = {} as Record<string, ILineType>;
  let ltype = {} as ILineType;
  let length = 0;
  let ltypeName: string | undefined;

  log.debug('LType {');
  let curr = scanner.next();
  while (!groupIs(curr, 0, DXFSymbol.EndTable)) {
    const property = lineTypePropertyFromCode.get(curr.code);
    if (property != null) {
      (ltype[property] as string | number) = curr.value as string | number;
      curr = scanner.next();
    } else {
      switch (curr.code) {
        case 2:
          ltype.name = curr.value as string;
          ltypeName = curr.value as string;
          curr = scanner.next();
          break;
        case 49:
          if (!ltype.pattern) ltype.pattern = [];
          ltype.pattern.push(curr.value as string);
          curr = scanner.next();
          break;
        case 73: // Number of elements for this line type (dots, dashes, spaces);
          length = ltype.elementCount = curr.value as number;
          if (length > 0) ltype.pattern = [];
          curr = scanner.next();
          break;
        case 74:
          if (!ltype.patternTypes) ltype.patternTypes = [];
          ltype.patternTypes.push(curr.value as number);
          curr = scanner.next();
          break;
        case 75:
          if (!ltype.shapeNumbers) ltype.shapeNumbers = [];
          ltype.shapeNumbers.push(curr.value as number);
          curr = scanner.next();
          break;
        case 100:
          if (!ltype.subMarkerCount) {
            ltype.subMarkerCount = 1;
          } else {
            ltype.subMarkerCount++;
          }
          ltype[`subMarker${ltype.subMarkerCount}` as 'subMarker1'] =
            curr.value as string;
          curr = scanner.next();
          break;
        case 102:
          if (
            typeof curr.value === 'string' &&
            curr.value.startsWith('{') &&
            curr.value.length > 0
          ) {
            const name = curr.value.substring(1);
            if (!ltype.applicationGroups) ltype.applicationGroups = {};
            ltype.applicationGroups[name] = parseApplicationGroup(scanner);
          }
          curr = scanner.next();
          break;
        case 0:
          log.debug('}');
          if (ltype.pattern && length > 0 && length !== ltype.pattern.length)
            log.warn('lengths do not match on LTYPE pattern');
          if (typeof ltypeName === 'string') {
            ltypes[ltypeName] = ltype; //save last ltype
          }
          ltype = {} as ILineType;
          log.debug('LType {');
          curr = scanner.next();
          break;
        default:
          curr = scanner.next();
      }
    }
  }

  log.debug('}');
  //ltypeName should be defined in while loop above
  if (typeof ltypeName === 'string') {
    ltypes[ltypeName] = ltype; //save last ltype
  }
  return ltypes;
};

export const serializeLineTypes = function* (
  ltypes: Record<string, ILineType>,
): IterableIterator<string> {
  for (const lineType of Object.values(ltypes) as ILineType[]) {
    yield '0';
    yield DXFSymbol.LType;
    for (const [property, value] of Object.entries(lineType) as [
      keyof ILineType,
      ILineType[keyof ILineType],
    ][]) {
      const code = codeFromLineTypeProperty.get(property);
      if (code != null) {
        yield* serializeGroupValue(code, value as number | string);
      } else {
        switch (property) {
          case 'patternLength':
            if (!lineType.pattern) {
              yield* serializeGroupValue(40, value as number);
            }
            break;
          case 'elementCount':
            yield* serializeGroupValue(73, value as number);
            if (lineType.pattern?.length !== value) {
              log.warn(
                `elementCount ${value} does not match pattern length ${lineType.pattern?.length}`,
              );
            }
            break;
          case 'pattern':
            if (lineType.patternLength) {
              yield* serializeGroupValue(40, lineType.patternLength as number);
            }
            for (const [i, p] of (value as string[]).entries()) {
              yield* serializeGroupValue(49, p);
              if (lineType.patternTypes && lineType.patternTypes[i] != null) {
                yield* serializeGroupValue(74, lineType.patternTypes[i]);
              }
              if (lineType.shapeNumbers && lineType.shapeNumbers[i] != null) {
                yield* serializeGroupValue(75, lineType.shapeNumbers[i]);
              }
            }
            break;
          case 'applicationGroups':
            yield* serializeApplicationDefinedGroups(
              value as ApplicationDefinedGroups,
            );
            break;
        }
      }
    }
  }
};
